import { Download, Eye, FileText, Receipt, Truck } from "lucide-react";
import { Button } from "../ui/button";
import {
  downloadAssetShipmentDocument,
  filterEwayBillDocuments,
  filterTaxInvoiceDocuments,
  type AssetShipmentDocument,
} from "../../lib/assetShipmentDocuments";

interface AssetShipmentDocumentsSectionProps {
  documents: AssetShipmentDocument[];
  loading?: boolean;
}

function DocumentRow({ doc }: { doc: AssetShipmentDocument }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/50">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white border border-slate-100 shadow-sm">
          <FileText className="h-4 w-4 text-blue-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-slate-700 truncate" title={doc.name}>
            {doc.name}
          </p>
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">{doc.docs_type}</p>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(doc.url, "_blank")}
          className="h-8 px-3 rounded-lg border-slate-200 text-[#3f8b98] font-semibold text-[11px] hover:bg-slate-100"
        >
          <Eye className="h-3.5 w-3.5 mr-1" />
          View
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void downloadAssetShipmentDocument(doc)}
          className="h-8 px-3 rounded-lg border-slate-200 text-slate-600 font-semibold text-[11px] hover:bg-slate-100"
        >
          <Download className="h-3.5 w-3.5 mr-1" />
          Download
        </Button>
      </div>
    </div>
  );
}

function DocumentGroup({
  title,
  icon,
  documents,
  emptyLabel,
}: {
  title: string;
  icon: React.ReactNode;
  documents: AssetShipmentDocument[];
  emptyLabel: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
          {icon}
        </div>
        <h4 className="text-[12px] font-semibold text-slate-700">{title}</h4>
        {documents.length > 0 && (
          <span className="text-[10px] font-medium text-slate-400">({documents.length})</span>
        )}
      </div>
      {documents.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 px-3 py-5 text-center text-[11px] text-slate-500">
          {emptyLabel}
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AssetShipmentDocumentsSection({
  documents,
  loading = false,
}: AssetShipmentDocumentsSectionProps) {
  const taxInvoices = filterTaxInvoiceDocuments(documents);
  const ewayBills = filterEwayBillDocuments(documents);

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm space-y-5">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
          <Receipt className="h-4 w-4 text-blue-500" />
        </div>
        <h3 className="text-[13px] font-semibold text-slate-700">Invoice Documents</h3>
      </div>

      {loading ? (
        <div className="rounded-xl border border-dashed border-slate-200 px-3 py-5 text-center text-[11px] text-slate-500">
          Loading documents…
        </div>
      ) : (
        <>
          <DocumentGroup
            title="Tax Invoice"
            icon={<Receipt className="h-4 w-4 text-blue-500" />}
            documents={taxInvoices}
            emptyLabel="No tax invoice documents available."
          />
          <DocumentGroup
            title="E-Way Bill"
            icon={<Truck className="h-4 w-4 text-blue-500" />}
            documents={ewayBills}
            emptyLabel="No e-way bill documents available."
          />
        </>
      )}
    </div>
  );
}
